import axios from "axios";

// Client API for the external-integrations platform (SKE-3949/3950/3951 backend →
// SKE-3952/3953/3954/3955 UI). Bare same-origin axios against schedugram-server
// (session cookie + CSRF), like api/ga4.ts. Endpoints live under
// /api/integrations and return the `{ status, data }` envelope. A 403 with
// `code: "feature_disabled"` means the org lacks the `external-integrations`
// entitlement (SKE-3972) — callers treat it as "surface unavailable".

export type IntegrationSurface = "ideas" | "library" | "inbox";
export type CapabilityDirection = "source" | "destination";
export type ConnectionStatus =
  | "pending_config"
  // A save that successfully started the first sync flips the connection here
  // (SKE-3941): it clears "Needs setup" instantly and reads as live "Syncing…"
  // until the first ok run promotes it to `active`. A later save on an active
  // connection can briefly re-enter `syncing` too.
  | "syncing"
  | "active"
  | "error"
  | "paused"
  | "disconnected";

// The connection-level error taxonomy (SKE-3948 state machine). The first three
// are credential problems the user fixes by reconnecting; `sync_failing` means the
// credentials are fine but syncs keep erroring — reconnecting would NOT help, so
// the UI must not tell the user to reconnect for it.
export type ConnectionErrorCode =
  "invalid_credentials" | "refresh_failed" | "unhealthy" | "sync_failing";

export interface CatalogCapability {
  surface: IntegrationSurface;
  direction: CapabilityDirection;
  label: string;
}

// A run's lifecycle phase (SKE-3941). `queued`/`running` are IN-FLIGHT (the run row
// is written at sync START and finalised on completion); `succeeded`/`failed` are
// terminal. The UI keys the live "Syncing…" treatment off these rather than `ok`,
// which has no meaning until a run finishes.
export type SyncPhase = "queued" | "running" | "succeeded" | "failed";

export interface CatalogProvider {
  key: string;
  displayName: string;
  category: string;
  logo: string;
  // How the catalog card starts a connection (SKE-3995/3999). Absent / "oauth" ⇒ the
  // standard Nango Connect UI flow (use-connect-flow.ts). "silent" ⇒ no OAuth /
  // Connect popup: the card calls the provider's own connect endpoint directly
  // (RSS today, which has no credentials to authorize). The overview keys the
  // silent path off this, falling back to `key === "rss"` if the server hasn't
  // stamped it yet.
  connectMode?: "silent" | "oauth";
  // Visible but not connectable: either the availability config stages it, or no
  // capability is wired to a deployed sync in this environment. The overview renders
  // "Coming soon" and hides Connect (SKE-3941).
  comingSoon: boolean;
  // Deployable, but this org's plan tier doesn't reach it (SKE-4405): the overview
  // renders "Upgrade" in place of Connect. Optional so a pre-deploy payload (server
  // not yet shipped) reads as "no upgrade needed" rather than gating everything.
  requiresUpgrade?: boolean;
  capabilities: CatalogCapability[];
  connection: {
    id: string;
    status: ConnectionStatus;
    connected: boolean;
    configuredCapabilities?: {
      capability: string;
      enabled: boolean;
      lastRun?: { ok?: boolean; at?: string };
      activity?: { phase: string; since?: string };
      syncedTotals?: { added: number };
      // Library only: at least one folder is paused on a deleted collection.
      needsSetup?: boolean;
      // Library only: one entry per folder (SKE-3963). Several folders may feed one
      // collection, so a collection can appear in more than one entry, and removed
      // folders are listed as `stopped`.
      collectionTargets?: LibraryCollectionTarget[];
    }[];
    // Widened overview payload (SKE-3955): `error.code` lets the overview tell a
    // credential failure (reconnect) apart from `sync_failing` (don't reconnect),
    // and `lastRun.ok` surfaces the two-tier "active but last sync had issues"
    // signal on the card without opening the detail view. Both optional/defensive.
    error?: { code?: ConnectionErrorCode };
    // Newest run across the connection's variants: `ok` + when it ran, so the card
    // shows "last synced X ago" without opening the detail view (SKE-3941).
    lastRun?: { ok?: boolean; at?: string };
    // Present only while a run is in flight (queued/running) — drives the live
    // "Syncing…" badge + pulsing dot on the card. `since` is when the run started.
    activity?: { phase: SyncPhase | string; since?: string };
    // Running tally of records added across the connection's variants, for an
    // at-a-glance "N imported" on the card.
    syncedTotals?: { added: number };
  } | null;
}

export type LibraryFolderStatus = "active" | "needs_setup" | "stopped";

// One library folder's read model, shared by the catalog's `collectionTargets` and the
// connection detail's per-capability `folders` (SKE-3963).
export interface LibraryCollectionTarget {
  collectionId: string;
  // The folder's sync variant: what "Sync now" and "Re-import everything" address.
  variant: string;
  folderId: string;
  folderName?: string;
  // Files currently imported from this folder.
  importedCount: number;
  lastSyncedAt?: string;
  status: LibraryFolderStatus;
  needsSetup?: "collection_deleted";
  removedAt?: string;
  lastRun?: { ok?: boolean; at?: string };
  activity?: { phase: string; since?: string };
}

export interface SyncStateEntry {
  variant: string;
  lastRun?: {
    added?: number;
    updated?: number;
    deleted?: number;
    at?: string;
    ok?: boolean;
  };
  cursor?: string | null;
  // Per-variant status extras the RSS feed list reads to render a feed's chip
  // (SKE-3995). All optional/defensive: when the server stamps an explicit
  // `status` the chip uses it verbatim; otherwise the client derives ok/warning/
  // failing from `lastRun.ok` and the connection's in-flight `activity`. `paused`
  // / `autoPaused` distinguish a hand-paused feed from one the server backed off
  // after repeated failures; `lastError` is the human error text shown when a feed
  // is failing.
  status?: "ok" | "syncing" | "warning" | "failing" | "paused" | "auto-paused";
  lastError?: string;
  paused?: boolean;
  autoPaused?: boolean;
}

export interface ConnectionCapabilityDetail {
  capability: string; // "<surface>:<direction>"
  label: string;
  config: Record<string, unknown> | null;
  enabled: boolean;
  syncState: SyncStateEntry[];
  // Library only (SKE-3963): at least one folder is paused on a deleted collection.
  needsSetup?: boolean;
  // Library only: one entry per folder, current folders first, then removed ones as
  // `stopped`.
  folders?: LibraryCollectionTarget[];
}

export interface ConnectionDetail {
  id: string;
  providerKey: string;
  status: ConnectionStatus;
  // How this connection re-links (SKE-3999). "silent" ⇒ an unauthenticated provider
  // (RSS) that re-links via the silent connect endpoint, NOT the OAuth reconnect
  // session — the detail view routes its "re-link" affordance accordingly and hides
  // the header Reconnect button. "oauth" ⇒ the standard hosted reconnect. Optional /
  // defensive: the UI treats an absent value as "oauth" (falling back to the SKE-3997
  // behavior) so an older payload still works.
  connectMode?: "silent" | "oauth";
  capabilities: ConnectionCapabilityDetail[];
  health?: { status: string; healthy: boolean };
  // Populated when `status === "error"`; `code` differentiates a credentials
  // problem (reconnect) from `sync_failing` (don't reconnect). Optional because
  // the payload only carries it while a connection is in the error state.
  error?: { code?: ConnectionErrorCode; message?: string; at?: string };
  // Roll-up of the most recent run across the connection's capabilities, used for
  // the two-tier "last sync had issues" signal when the connection itself is
  // still active. Optional/defensive: the run feed (getConnectionRuns) is the
  // authoritative source and the UI falls back to it when this is absent.
  lastRun?: {
    ok?: boolean;
    at?: string;
    added?: number;
    updated?: number;
    deleted?: number;
  };
  // In-flight runs (queued/running) for the live status header + timeline (SKE-3941).
  // Absent when nothing is syncing; one entry per in-flight (capability, variant).
  activity?: {
    capability: string;
    variant: string;
    phase: SyncPhase | string;
    since?: string;
  }[];
  // Cadence of the backing syncs — "hourly" for every provider today. Surfaced so the
  // detail can show "next sync in ~N min". Absent when the connection has no sync.
  frequency?: string;
  // Estimated next scheduled run = last run + one interval, when a last run is known
  // and the connection is actively syncing. A hint, not a guarantee.
  nextRunAt?: string;
  // Records landed across the connection's variants (sum of each variant's last-run
  // counts), the steady-state "total imported / health" summary. Only currently
  // configured variants count: removed folders and the webhook's duplicate row are
  // excluded server side (SKE-3963).
  totals?: { added: number; updated: number; deleted: number };
  // Ideas the sync gave up importing (ledger status "quarantined"): items that failed
  // every retry, so they are not in Sked and never will be until the cause is fixed.
  // `count` is the true total; `items` is a server-side cap of the most recent. Absent
  // when there are none, which is the case for every healthy connection, so the UI
  // renders nothing rather than an "all clear" box.
  blockedItems?: {
    count: number;
    items: {
      externalId: string;
      // Absent on rows written before the ledger carried a title; the UI falls back
      // to the external id so an item is always identifiable.
      title?: string;
      sourceUrl?: string;
      reason: string;
      at?: string;
    }[];
  };
}

// One row of the connection's run-history feed (GET .../runs, cursor-paginated,
// newest-first). `startedAt`/`finishedAt` arrive as ISO strings over the wire.
export interface SyncRunCounts {
  created?: number;
  // Library runs count new imports as `ingested` (consumeLibraryRecords metrics);
  // ideas runs use `created`. Both mean "added" to the run summary.
  ingested?: number;
  updated?: number;
  skipped?: number;
  deleted?: number;
  invalid?: number;
  // Rows the "Sync to Sked" gate filtered out. The server has always sent this; the
  // mirror was missing it, so a gated run rendered its skipped rows as nothing at all.
  excluded?: number;
  skippedOverCap?: number;
  errored?: number;
  // Set on a run row written because the provider REJECTED the trigger, so the sync
  // never started and there is no triad to summarise. Without it such a row rendered
  // as "Nothing to import" — a clean empty result for a request that failed.
  triggerRejected?: number;
  // Library runs: why files were skipped (SKE-3963). Each count is also in `skipped`.
  skippedByReason?: Partial<Record<LibrarySkipReason, number>>;
}

export type LibrarySkipReason =
  "unsupported" | "shortcut" | "native_doc" | "unchanged" | "source_deleted";

// Per-run mapping summary for an ideas run. Mirrors `IdeasRunDiagnostics` on the server
// (services/integrations/ideas/mappingDiagnostics.ts). Absent on library runs and on
// every run recorded before it shipped, so every field is optional here and the UI
// renders nothing when the object is missing, rather than a row of zeroes.
export interface IdeasRunDiagnostics {
  recordsRead?: number;
  // NOT a count of ideas: decided before sked-api is called, so it means the row was
  // mappable. `recordsLanded` is what actually arrived.
  recordsMapped?: number;
  // Rows sked-api actually created or updated. Absent on runs recorded before this
  // shipped, so render `recordsMapped` as the fallback rather than zero.
  recordsLanded?: number;
  // Rows sked-api deliberately left alone: create-only rows whose idea was edited in
  // Sked, and rows whose content has not changed.
  recordsSkipped?: number;
  // This run landed nothing AND the variant has never linked a row: the source has
  // never produced an idea, as opposed to an ordinary quiet sync or a planning sheet
  // nobody has ticked yet. Only the server can tell these apart (it checks the ledger).
  sourceHasNeverLanded?: boolean;
  // Rows the "Sync to Sked" gate filtered out.
  recordsExcluded?: number;
  // Rows that became ideas with no real title (blank cell, or the sync's "Untitled").
  titleFallback?: number;
  // Rows whose due date named a month and a day but no year, so Sked chose the nearest
  // occurrence to today. Reported because a guessed year a customer can see is
  // recoverable and one presented as fact is not. Present only when at least one row
  // needed the guess.
  datesYearInferred?: number;
  // Present only for a provider that names its source tabs (Google Sheets).
  rowsPerTab?: Record<string, number>;
  // Rows whose idea was reattached to a new position because the row SHIFTED rather
  // than changed, after someone inserted a row in a sheet with no id column. Present
  // only when at least one row moved.
  rowsReattached?: number;
  // The distinct values the Sync to Sked column actually held this run, capped by the
  // server. Present only when a gate is mapped and some row had a value in it.
  gateValuesSeen?: string[];
  // Records ignored because they were imported under the previous row-id scheme and
  // the provider is still replaying its cache of them. Present only while a Sheets
  // identity migration is settling.
  staleSchemeRecords?: number;
  // Rows the sync refused before they became records, because their id repeats another
  // row's on the same tab. Present only when at least one row was refused.
  rowsSkippedForDuplicateId?: number;
  // Source tabs the per-run record cap truncated, so moved rows could not be
  // reattached there. Absent on a healthy run, and absent on a RESUMED run that
  // finished a truncated tab without hitting the cap itself, so its absence is not
  // proof reconciliation worked.
  unreconciledTabs?: string[];
  // Columns the user mapped that resolved on no row this run. `reason` separates a
  // column that is not on the sheet ("missing") from one that is there and blank
  // ("empty"); absent when the run could not tell, which is every run recorded before
  // the sync began stating its column inventory. Typed as a plain string, like
  // `warnings`, so a reason the server adds later cannot fail to compile here.
  deadMappings?: { target: string; source: string; reason?: string }[];
  // Widened past the server's two current kinds ("mostly_untitled", "dead_mapping") so
  // the client cannot fail to compile against a warning kind the server adds later.
  warnings?: string[];
}

export interface SyncRun {
  id: string;
  capability: string;
  variant: string;
  // Lifecycle phase (SKE-3941). In-flight rows (queued/running, no `finishedAt`)
  // render live at the top of the feed; `ok` is meaningless until a run finishes.
  phase?: SyncPhase | string;
  startedAt?: string;
  finishedAt?: string;
  ok: boolean;
  counts: SyncRunCounts | null;
  sampledErrors: { externalId?: string; error: string }[];
  // Sibling of `counts`, not folded into it: the counts are the run's outcome, the
  // diagnostics explain it. Absent on every run row written before the server change.
  diagnostics?: IdeasRunDiagnostics;
  // Why a failed run failed, when one reason explains it: "collection_deleted" for a
  // library folder whose collection was deleted in Library (SKE-3963).
  reason?: string;
  // Set on a row that records a config change rather than a sync.
  event?: "folder_removed";
  folder?: {
    folderId: string;
    folderName?: string;
    targetCollectionId?: string;
    keptCount: number;
  };
}

export interface SyncRunPage {
  runs: SyncRun[];
  // Opaque keyset cursor when more pages remain; null on the last page. Pass it
  // back as `before` verbatim to fetch the next (older) page.
  nextCursor: string | null;
}

export interface ConnectSession {
  token: string;
  expiresAt: string;
  connectionId?: string;
}

// A single mappable field in a capability's field inventory (mirrors the server's
// IntegrationFieldDescriptor). `id` is the stable key the mapping engine + stored
// `fieldMappings` use; `label` is the human name the editor renders; `type` lets
// the editor hint at obvious mismatches; `required` (target-only) flags a field
// the editor must keep mapped.
export type IntegrationFieldType = "string" | "string[]" | "date" | "object";

export interface FieldDescriptor {
  id: string;
  label: string;
  type: IntegrationFieldType;
  required?: boolean;
}

// The field inventory the ideas mapping editor renders from (GET
// .../capabilities/{capability}/fields): SOURCE fields to map FROM, TARGET slots
// to map INTO, and the per-provider default mapping (target id → source id) the
// editor pre-populates. Target ids match the stored `fieldMappings` keys so the
// editor's output round-trips straight back into the mapping engine.
export interface CapabilityFieldSchema {
  source: FieldDescriptor[];
  target: FieldDescriptor[];
  defaultMappings: Record<string, string>;
  // SKE-4138: server-authoritative flag — true when this provider shows a PER-RESOURCE
  // dynamic column-mapping editor (Google Sheets / Monday / Notion / ClickUp / Trello)
  // and hides the single row-level editor. Replaces the client's old hardcoded
  // dynamic-provider set. Asana is NOT flagged (static single editor, FR2.3). Absent on
  // unscoped lookups ⇒ treat as false.
  hasResourceMapping?: boolean;
  // This provider can dry run a configuration against the real source. Where it can,
  // the screen shows what the source would REALLY produce; where it cannot, it falls
  // back to a worked example built from the mapping. The distinction matters: a
  // fabricated sample once let a customer save a configuration that imported nothing at
  // all for weeks, and the sample is exactly why it looked fine.
  hasPreview?: boolean;
  // The update mode a connection of this provider runs under when its stored config
  // leaves `updateMode` unset (registry.ts `defaultUpdateMode`; Google Sheets sets
  // "keep-updated"). The consumer resolves it at run time and never writes it into
  // stored config, so the editor must fall back the same way: an absent value means the
  // mapping engine's own "create-only", and the control must never claim a behaviour
  // the server is not running.
  defaultUpdateMode?: "create-only" | "keep-updated";
  // True when the provider emits no per-item timestamp, so "has this item changed" is
  // answered by comparing mapped content (registry.ts `contentResync`; Google Sheets
  // only, today). It changes what "create only" MEANS: on a content-resync provider a
  // changed row still reaches an idea nobody has edited inside Sked, while everywhere
  // else an imported item is never updated again. The update-mode copy reads this so
  // the two cases are not described with one wrong sentence.
  contentResync?: boolean;
}

// Typed shapes for the capability configs the Settings pickers build and PUT.
// They mirror the required fields of the server registry's config schemas
// (registry.ts): ideas needs a target brand group + ≥1 resource; library needs a
// target content-inbox collection + a single folder. The server validates the
// full schema (including optional fields the UI doesn't surface yet), so these
// only cover what the pickers set — `saveCapabilityConfig` still takes `unknown`.
// One independent ideas config → its own target brand group + resource set, and its
// own field mapping / status buckets / backfill window. Each entry becomes an
// independent Nango sync variant server-side, keyed by `id` (the variant name), so a
// single connection can pull several project sets at once, each into a different
// group. Mirrors LibraryFolderConfig's repeatable-row shape.
export interface IdeasConfigEntry {
  // Stable per-row id; IS the Nango variant name, so it must survive re-saves and
  // match /^[a-zA-Z0-9_-]{1,64}$/. A config migrated from the legacy single-config
  // shape keeps id "base" (the existing base variant — no re-import); new rows get a
  // freshly generated token.
  id: string;
  groupId: string;
  resourceIds: string[];
  // create-only ⇒ never touch an idea after first import; keep-updated ⇒ re-sync
  // changes from the source. Omitted ⇒ the server default. Pass-through today.
  updateMode?: "create-only" | "keep-updated";
  // Per-source-status routing into an idea's stage/status. A source status VALUE →
  // { stage: "pad" | "working", status? }. Editable per-resource via the status
  // editor (SKE-3985); a row-level value is the fallback when a candidate's resource
  // has no per-resource routing. Preserved across saves.
  statusBuckets?: IdeasStatusBuckets;
  // Optional per-config field remap (SKE-3957, widened SKE-3985): each Idea TARGET
  // slot → the provider SOURCE field id it reads from. Unset keys fall back to the
  // provider default, so an absent object is a no-op. The editor omits keys equal to
  // the default. Maps the row-level (static/base) source fields.
  fieldMappings?: IdeasFieldMappings;
  // Per-resource dynamic-column + status-routing overrides (SKE-3985), keyed by the
  // resource id a candidate came from. Each selected board/database/list/sheet maps
  // its OWN columns (via the `field:<colId>` source id) and status values; the server
  // applies the matching block over the row-level mapping (per-resource wins).
  resourceMappings?: IdeasResourceMappings;
  // Initial-import scope. Applied only on this config's first run to bound the
  // backfill of a large project; omitted ⇒ the server default (30d).
  backfillWindow?: BackfillWindow;
}

// Idea TARGET slot → provider SOURCE field id. Keys are the Idea targets the mapping
// engine writes; values are the source field id each reads from. A row-level mapping
// reads the static curated / core source fields; a per-resource mapping additionally
// reads that resource's dynamic columns via the `field:<colId>` source id. All
// optional — an absent key falls back to the provider default (or leaves the target
// unmapped when there is none).
export interface IdeasFieldMappings {
  title?: string;
  description?: string;
  labels?: string;
  dueDate?: string;
  status?: string;
  link?: string;
  caption?: string;
  hashTagsKeywords?: string;
  creativeBrief?: string;
  copyBrief?: string;
  // SKE-4138 "Sync to Sked" gate: the SOURCE column whose per-item value decides whether
  // the item is imported at all. Not an Idea field — it round-trips here (and inside
  // resourceMappings) so the gate stores like any other mapping. Unset ⇒ import every
  // item. Dynamic columns use the `field:<colId>` form.
  syncToSked?: string;
}

// Source status VALUE → the Idea stage / status it routes into. `stage` is the Idea
// pad ("pad") or Workspace ("working"); `status` is an optional Idea status string.
export type IdeasStatusBuckets = Record<
  string,
  { stage?: string; status?: string }
>;

// Per-resource override: this resource's own dynamic-column field mapping + status
// routing. Both optional so a resource can carry just a status routing (no column
// remap) or vice versa.
export interface IdeasResourceMapping {
  fieldMappings?: IdeasFieldMappings;
  statusBuckets?: IdeasStatusBuckets;
  // Which values of this resource's gate column mean "import this row". Absent keeps
  // the built-in affirmative set (a ticked box, yes, true, 1 and friends); present, it
  // REPLACES that set, which is what lets a status column act as the gate.
  syncToSkedValues?: string[];
}
export type IdeasResourceMappings = Record<string, IdeasResourceMapping>;

// One dynamic column of a selected resource, from the describe-schema action
// (SKE-3985). `id` is the stable provider column/property id the `field:<id>` source
// convention references; `name` is the human label; `type` is the provider's own
// column type (freeform — used only as a hint).
export interface IdeasSchemaColumn {
  id: string;
  name: string;
  type: string;
}

// One selectable source-status option of a resource, from describe-schema — real
// Monday labels / Notion status options / ClickUp statuses / Trello lists. Feeds the
// status-routing editor's value list.
export interface IdeasStatusOption {
  value: string;
  label: string;
}

// A selected resource's dynamic schema for the mapping editor: its columns and its
// status/stage option values. Empty columns ⇒ the provider has no dynamic discovery
// (static-only mapping). Mirrors the server's ResourceSchema.
export interface ResourceSchema {
  columns: IdeasSchemaColumn[];
  statusOptions?: IdeasStatusOption[];
}

// The ideas-source config: one or more independent config entries. Mirrors
// LibrarySourceConfig's `folders` list. A connection saved before multi-config
// carried the flat `{ groupId, resourceIds, ... }` shape (no `configs`); the form
// seeds that into a single `id: "base"` entry.
export interface IdeasSourceConfig {
  configs: IdeasConfigEntry[];
  // Optional global cap on records imported per run across all configs. Omitted ⇒
  // the server default.
  maxRecordsPerRun?: number;
}

// --- RSS feeds (SKE-3995) ----------------------------------------------------
// The rss provider's ideas:source config is a repeatable list of FEEDS rather than
// the generic `IdeasConfigEntry` list — each feed carries its own URL, cadence,
// filters, and idea field mapping. A dedicated config surface
// (integrations-hub/rss/rss-feed-config.tsx) replaces the generic CapabilityConfig
// for this provider, but it persists through the SAME save endpoint
// (PUT .../config, capability "ideas:source"). Each feed's `id` IS its Nango sync
// variant token (server derives the same via variantForFeed), so it must survive
// re-saves and match /^[a-zA-Z0-9_-]{1,64}$/ and never be "base".

export type RssFrequency = "30m" | "1h" | "6h" | "24h";
export type RssBackfill = "all" | "last-10" | "none";
// Which stage a synced item lands in: the Idea Pad ("pad", the default inbox) or
// straight into the Workspace ("working").
export type RssStage = "pad" | "working";

export interface RssFeedFilters {
  includeKeywords?: string[];
  excludeKeywords?: string[];
  includeCategories?: string[];
  excludeCategories?: string[];
}

// The idea TARGET slot → RSS SOURCE field mapping for a feed. Keys are the idea
// targets; values are the RSS source field id each reads from (title / summary /
// content / author / pubDate / categories). An absent key falls back to the
// provider default mapping.
export interface RssFieldMappings {
  title?: string;
  description?: string;
  caption?: string;
  hashTagsKeywords?: string;
  creativeBrief?: string;
  copyBrief?: string;
  labels?: string;
  dueDate?: string;
}

export interface RssFeedConfigEntry {
  id: string; // variant token — stable across saves, /^[a-zA-Z0-9_-]{1,64}$/, never "base"
  feedUrl: string;
  feedTitle?: string;
  groupId: string;
  frequency?: RssFrequency;
  backfill?: RssBackfill;
  filters?: RssFeedFilters;
  labelIds?: string[];
  autoCreateLabelsFromCategories?: boolean;
  mediaIngest?: boolean;
  fieldMappings?: RssFieldMappings;
  htmlMode?: "markdown" | "plain";
  stage?: RssStage;
  pubDateAsScheduledAt?: boolean;
  addedAt?: string;
}

export interface RssIdeasSourceConfig {
  configs: RssFeedConfigEntry[];
  maxRecordsPerRun?: number;
}

// One media enclosure on a validated feed item (SKE-3995). `kind` lets the preview
// render an image vs. a video placeholder; `thumbnailUrl` is a poster for videos.
export interface RssMediaItem {
  url: string;
  kind: "image" | "video";
  thumbnailUrl?: string;
}

// One sample item from a validated feed — the shape the add-feed wizard renders in
// its preview and the "resulting idea" is computed from.
export interface RssSampleItem {
  title: string;
  summary?: string;
  content?: string;
  link: string;
  author?: string;
  publishedAt?: string;
  categories?: string[];
  media?: RssMediaItem[];
}

// The result of validating / discovering a feed URL (POST .../rss/validate-feed).
// `url` is the CANONICAL feed url after discovery (given an HTML page URL the server
// may resolve the linked feed and return it here — the wizard stores this, not the
// typed input). `detected` flags which capabilities the feed exposes so the wizard
// can hint at media / category / author mapping. `error` carries a human reason when
// `ok` is false.
export interface ValidateRssFeedResult {
  ok: boolean;
  url: string;
  feedTitle?: string;
  itemCount?: number;
  dialect?: "rss2" | "atom" | "rdf";
  detected: { hasMedia: boolean; hasCategories: boolean; hasAuthors: boolean };
  sampleItems: RssSampleItem[];
  error?: string;
}

// The connection created / attached by the silent RSS connect (POST .../rss/connect).
export interface RssConnectResult {
  connectionId: string;
}

// How far back the FIRST sync run imports. Later runs are always incremental.
export type BackfillWindow = "30d" | "90d" | "all";

// One synced library folder → its own target collection. Each entry becomes an
// independent Nango sync variant server-side (SKE-3962), so a connection can pull
// from several folders at once, each landing in a different collection.
export interface LibraryFolderConfig {
  // Google Drive stores the folder id; Dropbox stores the folder path. Both flow
  // through this single `folderId` field (for Dropbox, id === path).
  folderId: string;
  // Human label for the picked folder, shown when re-opening the saved config.
  folderName?: string;
  targetCollectionId: string;
}

export interface LibrarySourceConfig {
  // May be empty: removing the last folder stops library syncing and keeps the
  // connection (SKE-3963).
  folders: LibraryFolderConfig[];
  // Initial-import scope, applied per folder on its first run; omitted ⇒ 30d.
  backfillWindow?: BackfillWindow;
}

// Inbox destinations: the default ticket fields applied when a conversation is
// escalated into the destination system. All optional — an omitted field means
// "no default". Consumed by escalation (SKE-3956/C1).
export interface InboxDestinationConfig {
  defaultPriority?: string;
  defaultTags?: string[];
  defaultAssigneeId?: string;
}

interface Envelope<T> {
  status: string;
  data: T;
}

function unwrap<T>(res: { data: Envelope<T> }): T {
  return res.data.data;
}

export async function getCatalog(): Promise<CatalogProvider[]> {
  const res =
    await axios.get<Envelope<{ providers: CatalogProvider[] }>>(
      "/api/integrations"
    );
  return res.data.data.providers;
}

export async function getConnection(id: string): Promise<ConnectionDetail> {
  return unwrap(
    await axios.get<Envelope<ConnectionDetail>>(
      `/api/integrations/connections/${id}`
    )
  );
}

// Run-history feed for a connection (newest-first). `before` is the opaque cursor
// returned as `nextCursor` on the previous page; `limit` defaults to 20 server-side.
export async function getConnectionRuns(
  connectionId: string,
  params?: { limit?: number; before?: string }
): Promise<SyncRunPage> {
  return unwrap(
    await axios.get<Envelope<SyncRunPage>>(
      `/api/integrations/connections/${connectionId}/runs`,
      {
        params: {
          ...(params?.limit ? { limit: params.limit } : {}),
          ...(params?.before ? { before: params.before } : {}),
        },
      }
    )
  );
}

// The field inventory the ideas mapping editor renders. Provider-agnostic in v1
// (keyed by the capability key alone), but `provider` now scopes it to a specific
// provider's source/target inventory when the fields differ — RSS exposes
// title/summary/content/author/pubDate/categories sources and the idea targets it
// maps into (SKE-3995). Omit `provider` for the shared ideas inventory. Read-only.
export async function getCapabilityFields(
  capability: string,
  provider?: string
): Promise<CapabilityFieldSchema> {
  return unwrap(
    await axios.get<Envelope<CapabilityFieldSchema>>(
      `/api/integrations/capabilities/${encodeURIComponent(capability)}/fields`,
      provider ? { params: { provider } } : undefined
    )
  );
}

// Silent RSS connect (SKE-3995): the RSS provider has no credentials to authorize,
// so instead of the Nango Connect UI the card POSTs here to create/attach the
// connection directly, then routes to the RSS feed config surface. Tolerates either
// a `{ connectionId }` payload or a full connection detail (reads `.id`).
export async function connectRssSilently(): Promise<RssConnectResult> {
  const data = unwrap(
    await axios.post<Envelope<{ connectionId?: string; id?: string }>>(
      `/api/integrations/rss/connect`
    )
  );
  return { connectionId: data.connectionId ?? data.id ?? "" };
}

// Validate / discover an RSS feed URL (SKE-3995). Given a feed URL it returns the
// feed title, a few sample items, and the detected capabilities; given an HTML page
// URL the server may resolve the linked feed and return its canonical `url`. Used
// by the add-feed wizard's first step.
export async function validateRssFeed(
  url: string
): Promise<ValidateRssFeedResult> {
  return unwrap(
    await axios.post<Envelope<ValidateRssFeedResult>>(
      `/api/integrations/rss/validate-feed`,
      { url }
    )
  );
}

export async function createConnectSession(
  provider: string
): Promise<ConnectSession> {
  return unwrap(
    await axios.post<Envelope<ConnectSession>>(
      `/api/integrations/${provider}/connect-session`
    )
  );
}

export async function createReconnectSession(
  connectionId: string
): Promise<ConnectSession> {
  return unwrap(
    await axios.post<Envelope<ConnectSession>>(
      `/api/integrations/connections/${connectionId}/reconnect-session`
    )
  );
}

// Attach a just-authorized Nango connection to this org immediately, using the
// connectionId the Connect UI returns on success — the webhook-independent path
// (so a connection appears without waiting on Nango's auth webhook).
export async function reconcileConnection(
  provider: string,
  connectionId: string
): Promise<{ status: ConnectionStatus }> {
  return unwrap(
    await axios.post<Envelope<{ status: ConnectionStatus }>>(
      `/api/integrations/${provider}/reconcile`,
      { connectionId }
    )
  );
}

// The outcome of a config save (SKE-3941). `synced:true` means the sync was started
// (and the connection flipped to `syncing`). `activated:true` means a destination
// (action-only, no sync) was configured and promoted to active — a success, not a
// failed sync. `synced:false` (without `activated`) is discriminated by `reason` so the
// client surfaces an actionable state instead of a false success:
//   `no_connection` — the mapping is stored but there's no connection to sync yet;
//   `start_failed`  — the sync-start call threw (mapping saved, sync didn't start).
export interface SaveCapabilityConfigResult {
  synced: boolean;
  activated?: boolean;
  reason?: "no_connection" | "start_failed";
  // The upstream reason behind a `start_failed`, so the retry banner can say what
  // went wrong rather than only that something did. Absent on success.
  detail?: string;
}

export async function saveCapabilityConfig(
  connectionId: string,
  capability: string,
  config: unknown
): Promise<SaveCapabilityConfigResult> {
  return unwrap(
    await axios.put<Envelope<SaveCapabilityConfigResult>>(
      `/api/integrations/connections/${connectionId}/config`,
      { capability, config }
    )
  );
}

export async function listResources(
  connectionId: string,
  capability: string,
  input?: unknown
): Promise<{ resources: unknown; cached: boolean }> {
  return unwrap(
    await axios.post<Envelope<{ resources: unknown; cached: boolean }>>(
      `/api/integrations/connections/${connectionId}/resources`,
      { capability, input }
    )
  );
}

// Describe a SELECTED resource's dynamic schema for the mapping editor (SKE-3985):
// its columns (`field:<colId>` source candidates) + status/stage option values.
// POSTs the resource id to the schema endpoint, which triggers the capability's
// describe-schema action. Providers without dynamic discovery return empty columns.
// Short-cached like listResources (the hook sets staleTime).
export async function describeCapabilitySchema(
  connectionId: string,
  capability: string,
  resourceId: string
): Promise<{ schema: ResourceSchema; cached: boolean }> {
  return unwrap(
    await axios.post<Envelope<{ schema: ResourceSchema; cached: boolean }>>(
      `/api/integrations/connections/${connectionId}/schema`,
      { capability, resourceId }
    )
  );
}

// The dry run: what this configuration would ACTUALLY import, from the real source,
// through the real mapper. Takes the UNSAVED row off the screen, which is the point --
// previewing the saved config would show the mapping as it was before editing started.
// A POST because the body is the config, not because it writes anything; it writes
// nothing.
export interface IdeasPreviewFinding {
  kind: string;
  severity: "blocking" | "warning" | "info";
  tab?: string;
  count?: number;
  values?: string[];
}
export interface IdeasPreviewSample {
  externalId: string;
  excluded: boolean;
  fields: {
    title?: string;
    description?: string;
    dueDate?: string | null;
    status?: string;
    stage?: string;
    labels?: string[];
  };
  gateValue?: string;
  dueDateYearInferred?: boolean;
}
export interface IdeasPreviewTab {
  title: string;
  verdict: "read" | "skipped";
  skipReason?: string;
  headerRow: string[];
  identity: "keyed" | "positional";
  rowsRead: number;
  wouldImport: number;
  wouldExclude: number;
  samples: IdeasPreviewSample[];
}
export interface IdeasPreviewResource {
  resourceId: string;
  title?: string;
  tabs: IdeasPreviewTab[];
}
export interface IdeasImportPreview {
  resources: IdeasPreviewResource[];
  findings: IdeasPreviewFinding[];
  rowsRead: number;
  wouldImport: number;
  wouldExclude: number;
}

export async function previewIdeasConfig(
  connectionId: string,
  entry: {
    resourceIds: string[];
    fieldMappings?: IdeasFieldMappings;
    statusBuckets?: IdeasStatusBuckets;
    resourceMappings?: IdeasResourceMappings;
    syncToSkedValues?: string[];
    updateMode?: "create-only" | "keep-updated";
  }
): Promise<IdeasImportPreview> {
  return unwrap(
    await axios.post<Envelope<IdeasImportPreview>>(
      `/api/integrations/connections/${connectionId}/preview-ideas`,
      entry
    )
  );
}

export async function pauseConnection(connectionId: string): Promise<void> {
  await axios.post(`/api/integrations/connections/${connectionId}/pause`);
}

export async function resumeConnection(connectionId: string): Promise<void> {
  await axios.post(`/api/integrations/connections/${connectionId}/resume`);
}

// `refreshFailed`: a library folder awaiting its re-import could not get the full
// refresh from the source (the same flag the reimport route returns).
export interface TriggerSyncResult {
  triggered: true;
  refreshFailed?: boolean;
}

// `variant` syncs one library folder (SKE-3963); absent syncs every configured variant.
export async function triggerConnectionSync(
  connectionId: string,
  variant?: string
): Promise<TriggerSyncResult> {
  const url = `/api/integrations/connections/${connectionId}/trigger-sync`;
  return unwrap(
    variant
      ? await axios.post<Envelope<TriggerSyncResult>>(url, { variant })
      : await axios.post<Envelope<TriggerSyncResult>>(url)
  );
}

// Re-import one ideas config entry under the current mapping (E0/E1c). `state` says
// what happened server-side: `reset` re-ran an existing source, `never_synced` started
// its first import, and `in_progress` means a sync was already running so nothing was
// queued. The caller keys its toast on that rather than assuming success.
export type ReimportIdeasState = "reset" | "in_progress" | "never_synced";

// The re-import started, but the provider could not be asked for its latest changes.
// A 200 and not an error on purpose: the rows already imported ARE being re-mapped,
// which is the whole reason the button exists. Only provider-side changes made since
// the last sync are deferred to the next scheduled one.
export interface ReimportIdeasResult {
  state: ReimportIdeasState;
  refreshFailed?: boolean;
}

export async function reimportIdeas(
  connectionId: string,
  capability: string,
  variant: string
): Promise<ReimportIdeasResult> {
  return unwrap(
    await axios.post<Envelope<ReimportIdeasResult>>(
      `/api/integrations/connections/${connectionId}/reimport`,
      { capability, variant }
    )
  );
}

// "Re-import everything" for one library folder (SKE-3963): the server resets that
// folder's ledger and re-pulls it in full, restoring files deleted in Sked Social.
export async function reimportLibraryFolder(
  connectionId: string,
  variant: string
): Promise<ReimportIdeasResult> {
  return unwrap(
    await axios.post<Envelope<ReimportIdeasResult>>(
      `/api/integrations/connections/${connectionId}/reimport`,
      { capability: "library:source", variant }
    )
  );
}

export async function disconnectConnection(
  connectionId: string
): Promise<void> {
  await axios.delete(`/api/integrations/connections/${connectionId}`);
}

export type DiscoveryPlacement = "onboarding" | "home" | "ideas" | "library";
export interface DiscoveryPreference {
  placement: DiscoveryPlacement;
  impressions: number;
  permanent: boolean;
  snoozedUntil?: string;
}
export async function getDiscoveryPreferences(): Promise<
  DiscoveryPreference[]
> {
  return unwrap(
    await axios.get<Envelope<DiscoveryPreference[]>>(
      "/api/integrations/discovery"
    )
  );
}
export async function claimDiscovery(
  placement: DiscoveryPlacement,
  sessionId: string
): Promise<{ show: boolean; newImpression: boolean }> {
  return unwrap(
    await axios.post<Envelope<{ show: boolean; newImpression: boolean }>>(
      `/api/integrations/discovery/${placement}/claim`,
      { sessionId }
    )
  );
}
export async function dismissDiscovery(
  placement: DiscoveryPlacement,
  permanent: boolean
): Promise<void> {
  await axios.post(`/api/integrations/discovery/${placement}/dismiss`, {
    permanent,
  });
}
